// frontend/src/lib/sentry.ts
/**
 * Sentry error monitoring configuration.
 * Captures JavaScript errors, unhandled promise rejections, and failed network requests.
 */

import * as Sentry from '@sentry/react';
import type { SeverityLevel } from '@sentry/react';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

/**
 * Initialize Sentry error monitoring.
 * Only initializes in production or if DSN is explicitly set.
 */
export const initSentry = (): void => {
  if (!SENTRY_DSN) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Sentry: Skipping initialization (no DSN configured)');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV || 'development',

    // Only send errors in production by default
    enabled: process.env.NODE_ENV === 'production' || process.env.REACT_APP_SENTRY_ENABLED === 'true',

    sampleRate: 1.0,

    // Set to 0 to disable performance monitoring and save quota
    tracesSampleRate: 0,

    sendDefaultPii: false,

    ignoreErrors: [
      // Browser extensions
      /Extensions/i,
      /^chrome-extension:/,
      /^moz-extension:/,
      // Network errors that are expected
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      'NetworkError',
      // User-initiated cancellations
      'AbortError',
      // Benign React errors
      'ResizeObserver loop',
      // Auth errors we handle gracefully
      'Invalid Refresh Token',
      'Session expired',
    ],

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
        return null;
      }
      return breadcrumb;
    },

    beforeSend(event) {
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
      }

      if (event.message) {
        event.message = event.message.replace(
          /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
          '[email]'
        );
      }

      return event;
    },

    integrations: [
      Sentry.browserTracingIntegration({
        enableInp: false,
      }),
    ],
  });

  if (process.env.NODE_ENV === 'development') {
    console.log('Sentry: Initialized');
  }
};

export interface SentryUser {
  id: string;
}

/**
 * Set the current user context for error reports.
 * Call this after the user logs in.
 */
export const setSentryUser = (user: SentryUser | null): void => {
  if (!SENTRY_DSN) return;

  if (user) {
    Sentry.setUser({
      id: user.id,
      // Don't send email to avoid PII issues - use a hash if needed
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Add custom context to error reports.
 */
export const setSentryContext = (
  name: string,
  context: Record<string, unknown> | null
): void => {
  if (!SENTRY_DSN) return;
  Sentry.setContext(name, context);
};

/**
 * Manually capture an error.
 */
export const captureError = (
  error: unknown,
  context: Record<string, unknown> = {}
): void => {
  if (!SENTRY_DSN) {
    console.error('Error (Sentry disabled):', error, context);
    return;
  }

  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureException(error);
  });
};

/**
 * Manually capture a message.
 */
export const captureMessage = (
  message: string,
  level: SeverityLevel = 'info'
): void => {
  if (!SENTRY_DSN) {
    console.log(`Message (Sentry disabled) [${level}]:`, message);
    return;
  }

  Sentry.captureMessage(message, level);
};

// Re-export Sentry's ErrorBoundary for convenience
export const SentryErrorBoundary = Sentry.ErrorBoundary;

initSentry();

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  (window as Window & { Sentry?: typeof Sentry }).Sentry = Sentry;
}

export default Sentry;
